import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { ScanSearch, FileText, TriangleAlert, Filter, Route, GitBranch, Presentation, AlertTriangle, CheckCircle2, Cpu } from "lucide-react";
import { Panel, Ring, Tile, Pill, useCountUp } from "./ui";
import { nexsysApi } from "./api";
import { CompliancePosture } from "./CompliancePosture";
import { NeuralArea, RiskDonut, HeatMap, ForecastBand, DependencyNetwork } from "./charts";
import { TWIN_NODES, TWIN_EDGES, FLAGS, WEEKS, COMMANDS, sevColor, flagColor } from "./data";

const CMD_ICONS = { analyze: ScanSearch, brief: FileText, predict: TriangleAlert, bottleneck: Filter, optimize: Route, scenario: GitBranch, board: Presentation };

/* ==================== COMMAND CONSOLE ==================== */
function CommandConsole({ runCommand }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {COMMANDS.map((c) => {
        const Icon = CMD_ICONS[c.id] || ScanSearch;
        return (
          <button
            key={c.id} onClick={() => runCommand(c.msg)} data-testid={`cmd-${c.id}`}
            className="group flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5 text-left text-[12px] text-white/70 transition-all hover:border-cyan-400/40 hover:bg-cyan-400/10 hover:text-white"
          >
            <Icon className="h-3.5 w-3.5 shrink-0 text-cyan-400/70 group-hover:text-cyan-300" />
            <span className="leading-tight">{c.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ==================== OVERVIEW ==================== */
export function Overview({ portfolio, timeseries, tsMeta, insights, runCommand }) {
  const p = portfolio || {};
  if (!p.source) {
    return (
      <div className="space-y-4">
        <Panel title="Enterprise Portfolio Health" subtitle="NO LIVE DATA SOURCE" testid="overview-empty">
          <p className="text-sm text-white/60">No live data source is connected, so no portfolio figures are shown. Connect a source under Data Sources to populate this view.</p>
        </Panel>
        <CompliancePosture />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1.85fr)]">
        <Panel title="Enterprise Portfolio Health" subtitle="COMPOSITE INTELLIGENCE SCORE" testid="overview-score"
          right={<Pill tone="emerald">{p.source ? "LIVE" : "HEALTHY"}</Pill>}>
          <div className="flex flex-col items-center">
            <Ring value={p.intelligenceScore || 0} label="Portfolio Health" sub={`${p.activePrograms || 0} active programs`} />
            <div className="mt-4 grid w-full grid-cols-2 gap-2">
              {p.source
                ? <Mini label="Data Source" value="JIRA · LIVE" />
                : <Mini label="Capital Deployed" value={`$${p.capitalDeployed || 0}M`} />}
              <Mini label="Programs" value={p.activePrograms || 0} />
            </div>
          </div>
        </Panel>

        <Panel title="Executive Brief" subtitle={p.source ? "COMPOSED FROM LIVE CONNECTOR DATA" : "AI-GENERATED · CONFIDENCE 92%"} testid="overview-brief"
          right={p.source ? <Pill tone="emerald">LIVE · JIRA</Pill> : <Pill tone="cyan">GPT · MOCKED</Pill>}>
          <p className="text-sm leading-relaxed text-white/70">{p.executiveSummary || "Generating executive summary…"}</p>
          <div className="my-4 grid grid-cols-3 gap-3">
            <Callout label="Portfolio Health" value={`${Math.round(p.intelligenceScore || 0)}%`} tone="emerald" />
            <Callout label="Highest Risk" value={p.highestRisk || "ERP Modernization"} tone="magenta" small />
            {p.source
              ? <Callout label="Late Milestones" value={`${p.lateMilestones ?? 0}`} tone="cyan" />
              : <Callout label="Confidence" value={`${Math.round(p.aiConfidence || 0)}%`} tone="cyan" />}
          </div>
          <div className="mb-2 font-mono text-[10px] tracking-widest text-white/35">AI COMMAND CONSOLE</div>
          <CommandConsole runCommand={runCommand} />
        </Panel>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        {p.kpis ? p.kpis.map((k) => (
          <Tile key={k.testid} label={k.label} value={k.value} suffix={k.suffix} tone={k.tone}
            decimals={k.suffix === "%" ? 1 : 0} testid={k.testid} />
        )) : (<>
          <Tile label="AI Confidence" value={p.aiConfidence || 0} suffix="%" tone="cyan" trend={3} decimals={1} testid="kpi-ai" />
          <Tile label="Delivery Risk" value={p.deliveryRisk || 0} suffix="%" tone="magenta" trend={-2} decimals={1} testid="kpi-risk" />
          <Tile label="Budget Health" value={p.budgetHealth || 0} suffix="%" tone="emerald" trend={1} decimals={1} testid="kpi-budget" />
          <Tile label="Strategic Align" value={p.strategicAlignment || 0} suffix="%" tone="indigo" trend={4} decimals={1} testid="kpi-align" />
          <Tile label="Velocity" value={p.velocity || 0} suffix="%" tone="cyan" trend={6} decimals={1} testid="kpi-velocity" />
          <Tile label="Readiness" value={p.readiness || 0} suffix="%" tone="amber" trend={2} decimals={1} testid="kpi-readiness" />
        </>)}
      </div>

      <CompliancePosture />

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Portfolio Health Timeline" subtitle={tsMeta?.history ? "RECORDED AT EVERY LIVE SYNC" : "ACTUAL vs FORECAST"} className="lg:col-span-3" testid="overview-timeline"
          right={<div className="flex items-center gap-3 text-[10px] font-mono">
            {tsMeta?.history
              ? <Pill tone="emerald" data-testid="timeline-live-pill">LIVE HISTORY · {tsMeta.points} SYNCS</Pill>
              : p.source && <Pill tone="amber">DEMO SERIES</Pill>}
            {tsMeta?.history
              ? <span className="text-cyan-300">— HEALTH (RECORDED)</span>
              : <><span className="text-cyan-300">— ACTUAL</span><span className="text-indigo-300">-- FORECAST</span></>}
          </div>}>
          <NeuralArea data={timeseries} />
        </Panel>
      </div>

    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 text-center">
      <div className="font-display text-lg font-bold text-white">{value}</div>
      <div className="font-mono text-[9px] uppercase tracking-widest text-white/35">{label}</div>
    </div>
  );
}
function Callout({ label, value, tone, small }) {
  const c = { cyan: "#22d3ee", emerald: "#34d399", magenta: "#ec4899" }[tone];
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
      <div className="font-mono text-[9px] uppercase tracking-widest text-white/35">{label}</div>
      <div className={`mt-1 font-display font-bold ${small ? "text-sm" : "text-xl"}`} style={{ color: c }}>{value}</div>
    </div>
  );
}

/* ==================== RISK MONITORING ==================== */
function useLiveRisk() {
  const [live, setLive] = useState(null);
  useEffect(() => {
    nexsysApi.insights()
      .then((d) => {
        if (d?.risks_source && d?.risk_breakdown) {
          setLive({
            source: d.risks_source,
            breakdown: [
              { label: "Critical", value: d.risk_breakdown.Critical || 0, color: "#ff3b6b" },
              { label: "High", value: d.risk_breakdown.High || 0, color: "#ff9f43" },
              { label: "Moderate", value: d.risk_breakdown.Moderate || 0, color: "#22d3ee" },
              { label: "Low", value: d.risk_breakdown.Low || 0, color: "#34d399" },
            ],
            total: d.risk_total || 0,
            risks: d.risks || [],
            byProgram: d.risk_by_program || null,
          });
        }
      })
      .catch(() => {});
  }, []);
  return live;
}

export function RiskView({ runCommand }) {
  const live = useLiveRisk();
  if (!live) {
    return (
      <Panel title="Risk Monitoring" subtitle="NO LIVE RISK DATA" testid="risk-empty">
        <p className="text-sm text-white/60">No live risk data is available. Risk figures appear once a live data source is connected.</p>
      </Panel>
    );
  }
  const sevIcon = { critical: "#ff3b6b", high: "#ff9f43", moderate: "#22d3ee", low: "#34d399" };
  const breakdown = live.breakdown;
  const total = live.total;
  const criticalCount = breakdown.find((s) => s.label === "Critical")?.value ?? 0;
  const lowCount = breakdown.find((s) => s.label === "Low")?.value ?? 0;
  const lowRiskPct = total ? Math.round((100 * lowCount) / total) : 0;
  const signals = live.risks.slice(0, 5).map((r) => ({
        title: r.title, program: r.program, when: r.id,
        sev: (r.level || "low").toLowerCase(),
        w: { critical: 90, high: 70, moderate: 50, low: 30 }[(r.level || "low").toLowerCase()] || 40,
      }));
  const heatLevels = ["Critical", "High", "Moderate", "Low"];
  const maxCount = live?.byProgram
    ? Math.max(1, ...Object.values(live.byProgram).flatMap((c) => heatLevels.map((l) => c[l] || 0)))
    : 1;
  const heatMatrix = live?.byProgram
    ? Object.entries(live.byProgram).slice(0, 5).map(([prog, counts]) => ({
        label: prog,
        cells: heatLevels.map((lvl) => ({
          v: (counts[lvl] || 0) / maxCount,
          count: counts[lvl] || 0,
          title: `${prog} · ${counts[lvl] || 0} open ${lvl} risk(s) — live register`,
        })),
      }))
    : null;
  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <Panel title="Risk Monitoring" subtitle={live ? "PORTFOLIO-WIDE · LIVE" : "PORTFOLIO-WIDE · SAMPLE DATA"} testid="risk-monitor" right={<Pill tone="magenta">{criticalCount} CRITICAL</Pill>}>
          <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center">
            <RiskDonut segments={breakdown} total={total} />
            <div className="flex-1 space-y-2.5">
              {breakdown.map((s) => (
                <div key={s.label} className="flex items-center gap-3">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color, boxShadow: `0 0 8px ${s.color}` }} />
                  <span className="text-sm text-white/70">{s.label}</span>
                  <span className="ml-auto font-display text-lg font-bold text-white">{s.value}</span>
                </div>
              ))}
              <div className="mt-2 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 text-center">
                <span className="font-display text-2xl font-bold text-cyan-300">{lowRiskPct}%</span>
                <span className="ml-2 text-xs text-white/40">Low-risk exposure</span>
              </div>
            </div>
          </div>
        </Panel>
        <Panel title="Risk Heat Map" subtitle={heatMatrix ? "PROGRAM × SEVERITY · LIVE OPEN RISKS" : "PROGRAM × PHASE · SAMPLE DATA"} testid="risk-heatmap">
          <HeatMap rows={5} cols={8} matrix={heatMatrix} colLabels={heatMatrix ? ["Critical", "High", "Moderate", "Low"] : null} />
          <div className="mt-4 flex items-center justify-between font-mono text-[10px] text-white/40">
            <span>LOW</span>
            <div className="mx-3 h-1.5 flex-1 rounded-full bg-gradient-to-r from-[#12324a] via-[#22d3ee] to-[#ff3b6b]" />
            <span>CRITICAL</span>
          </div>
        </Panel>
      </div>

      <Panel title="Latest Risk Signals" subtitle={live ? "LIVE · FROM CONNECTED DATA" : "AI-DETECTED · SAMPLE DATA"} testid="risk-signals"
        right={<button onClick={() => runCommand("Predict delivery risk across programs.")} className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-[11px] text-cyan-300">Predict Risk</button>}>
        <div className="space-y-2">
          {signals.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
              className="flex items-center gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
              <AlertTriangle className="h-4 w-4 shrink-0" style={{ color: sevIcon[s.sev] }} />
              <div className="min-w-0">
                <div className="text-sm font-medium text-white/90">{s.title}</div>
                <div className="text-[11px] text-white/40">{s.program}</div>
              </div>
              <span className="ml-auto hidden font-mono text-[11px] text-white/35 sm:block">{s.when}</span>
              <div className="hidden h-2 w-32 overflow-hidden rounded-full bg-white/5 md:block">
                <motion.div initial={{ width: 0 }} animate={{ width: `${s.w}%` }} transition={{ delay: 0.2 + i * 0.06, duration: 0.8 }}
                  className="h-full rounded-full" style={{ background: sevIcon[s.sev], boxShadow: `0 0 8px ${sevIcon[s.sev]}` }} />
              </div>
            </motion.div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

/* ==================== TIMELINE FORECAST ==================== */
function useLivePrograms() {
  const [live, setLive] = useState(null);
  useEffect(() => {
    nexsysApi.programs()
      .then((d) => {
        if (d?.source && d.source !== "synthetic" && d.programs?.length) setLive({ source: d.source, programs: d.programs });
      })
      .catch(() => {});
  }, []);
  return live;
}

function useLiveMilestones() {
  const [live, setLive] = useState(null);
  useEffect(() => {
    nexsysApi.milestones()
      .then((d) => {
        if (d?.source && d.source !== "synthetic" && d.milestones?.length) setLive({ source: d.source, milestones: d.milestones });
      })
      .catch(() => {});
  }, []);
  return live;
}

export function TimelineView() {
  const live = useLivePrograms();
  const liveMs = useLiveMilestones();
  const rows = live
    ? live.programs.map((p) => ({
        name: p.name, start: 0, len: 12,
        flag: p.risk >= 65 ? "red" : p.risk >= 45 ? "yellow" : "green",
        pct: Math.round(p.progress),
      }))
    : [];
  const ownerByProgram = live ? Object.fromEntries(live.programs.map((p) => [p.name, p.owner])) : {};
  const deliverables = liveMs
    ? liveMs.milestones.slice(0, 8).map((m) => ({
        phase: m.phase, owner: ownerByProgram[m.program] || m.program, due: m.due,
        pct: m.pct, flag: m.flag === "done" ? "green" : m.flag === "late" ? "red" : "blue",
      }))
    : [];
  return (
    <div className="space-y-4">
      <Panel title="Timeline Forecast" subtitle={live ? `LIVE · ${live.source.toUpperCase()} CONNECTOR` : "12-WEEK PROGRAM HORIZON"} testid="timeline-gantt"
        right={<div className="flex flex-wrap gap-2">{FLAGS.map((f) => (
          <span key={f.key} className="flex items-center gap-1.5 text-[10px] text-white/50"><span className="h-2 w-2 rounded-sm" style={{ background: f.color }} />{f.label}</span>
        ))}</div>}>
        <div className="min-w-[560px] md:min-w-0">
          <div className="mb-2 grid grid-cols-12 gap-1 pl-40 font-mono text-[9px] text-white/30">
            {WEEKS.map((w) => <div key={w} className="text-center">{w}</div>)}
          </div>
          {rows.length === 0 && (<div className="py-6 text-center text-sm text-white/50">No live program data. Connect a live data source to populate the timeline.</div>)}
          <div className="space-y-2">
            {rows.map((g, i) => (
              <div key={g.name} className="flex items-center gap-3">
                <div className="w-40 shrink-0 truncate text-xs text-white/70">{g.name}</div>
                <div className="relative h-7 flex-1 rounded-lg bg-white/[0.02]">
                  <motion.div
                    initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: i * 0.08, duration: 0.7 }}
                    className="absolute top-1/2 flex h-5 -translate-y-1/2 items-center overflow-hidden rounded-md"
                    style={{
                      left: `${(g.start / 12) * 100}%`, width: `${(g.len / 12) * 100}%`, transformOrigin: "left",
                      background: `linear-gradient(90deg, ${flagColor(g.flag)}cc, ${flagColor(g.flag)}55)`,
                      boxShadow: `0 0 14px ${flagColor(g.flag)}55`,
                    }}>
                    <div className="h-full bg-white/20" style={{ width: `${g.pct}%` }} />
                    <span className="absolute right-2 font-mono text-[9px] text-white/80">{g.pct}%</span>
                  </motion.div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Panel>

      <div className="grid grid-cols-1 gap-4">
        <Panel title="Key Deliverables" subtitle={liveMs ? `MILESTONE TRACKING · LIVE · ${liveMs.source.toUpperCase()}` : "MILESTONE TRACKING · SAMPLE DATA"} testid="timeline-deliverables">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="font-mono text-[10px] uppercase tracking-wider text-white/35">
                  <th className="pb-2 font-normal">Phase</th><th className="pb-2 font-normal">Owner</th>
                  <th className="pb-2 font-normal">Status</th><th className="pb-2 font-normal">Due</th><th className="pb-2 font-normal">Progress</th>
                </tr>
              </thead>
              <tbody>
                {deliverables.map((d, i) => (
                  <motion.tr key={`${d.phase}-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.06 }} className="border-t border-white/[0.05]">
                    <td className="py-3 text-white/85">{d.phase}</td>
                    <td className="py-3 text-white/50">{d.owner}</td>
                    <td className="py-3"><span className="flex items-center gap-1.5 text-xs" style={{ color: flagColor(d.flag) }}><span className="h-2 w-2 rounded-sm" style={{ background: flagColor(d.flag) }} />{FLAGS.find((f) => f.key === d.flag)?.label}</span></td>
                    <td className="py-3 font-mono text-xs text-white/50">{d.due}</td>
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-white/5"><div className="h-full rounded-full" style={{ width: `${d.pct}%`, background: flagColor(d.flag) }} /></div>
                        <span className="font-mono text-[11px] text-white/50">{d.pct}%</span>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
            {deliverables.length === 0 && (<div className="py-6 text-center text-sm text-white/50">No live milestone data.</div>)}
          </div>
        </Panel>
      </div>
    </div>
  );
}

/* ==================== DIGITAL TWIN ==================== */
// Honest labeling: a node is LIVE only when a real probe backs it. Today that is the Data Center node, which is
// the application's self-probe of its own hosting environment. Every other node has no live connector, so its
// provisioning-queue value is shown as a stored label (never a green live status), or UNCONFIGURED.
const QUEUE_LABEL = {
  connected: "QUEUE: CONNECTED", ready: "QUEUE: READY", provisioning: "QUEUE: PROVISIONING",
  requested: "QUEUE: REQUESTED", error: "QUEUE: ERROR",
};
const TWIN_STATE = {
  live: { color: "#34d399", label: "LIVE · SELF-PROBE" },
  degraded: { color: "#ef4444", label: "PROBE FAILED" },
  checking: { color: "#94a3b8", label: "PROBING…" },
  not_live: { color: "#64748b", label: "NOT LIVE" },
};

function fmtUptime(s) {
  if (s == null) return "—";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
}

export function DigitalTwinView() {
  const [data, setData] = useState(null);
  const [host, setHost] = useState(null);
  const [hostErr, setHostErr] = useState(false);
  useEffect(() => {
    const load = () => {
      nexsysApi.integrations().then(setData).catch(() => setData(null));
      nexsysApi.hosting().then((h) => { setHost(h); setHostErr(false); }).catch(() => setHostErr(true));
    };
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const statusById = {};
  (data?.connectors || []).forEach((c) => { statusById[c.id] = c.status || "not_connected"; });
  const nodes = TWIN_NODES.map((n) => {
    if (n.probe === "hosting") {
      const st = hostErr ? "degraded" : host ? (host.status === "live" ? "live" : "degraded") : "checking";
      return { ...n, state: st, live: st === "live", color: TWIN_STATE[st].color, hollow: st !== "live",
        badge: TWIN_STATE[st].label, sub: "APP HOSTING · MEASURED" };
    }
    const q = statusById[n.connector] || "not_connected";
    return { ...n, state: "not_live", live: false, color: TWIN_STATE.not_live.color, hollow: true,
      badge: QUEUE_LABEL[q] || "UNCONFIGURED", sub: "NO LIVE CONNECTOR" };
  });
  const liveCount = nodes.filter((n) => n.live).length;
  const queueLabelled = nodes.filter((n) => !n.probe && QUEUE_LABEL[statusById[n.connector]]).length;
  const rt = host?.runtime || {};
  const dbi = host?.database || {};

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <Panel title="Enterprise Digital Twin" subtitle="TOPOLOGY · LIVE ONLY WHERE A PROBE EXISTS · OTHER NODES SHOW STORED QUEUE LABELS" testid="twin-graph"
        right={<Pill tone={liveCount ? "emerald" : "amber"}>{liveCount}/{TWIN_NODES.length} LIVE-PROBED</Pill>} bodyClass="p-2">
        <DependencyNetwork nodes={nodes} edges={TWIN_EDGES} />
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pb-2 font-mono text-[9px] uppercase tracking-wider text-white/40" data-testid="twin-legend">
          <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full" style={{ background: TWIN_STATE.live.color }} />LIVE PROBE OK</span>
          <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full" style={{ background: TWIN_STATE.degraded.color }} />PROBE FAILED</span>
          <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full border border-dashed" style={{ borderColor: TWIN_STATE.not_live.color }} />NOT LIVE · QUEUE LABEL / UNCONFIGURED</span>
        </div>
      </Panel>
      <div className="space-y-4">
        <Panel title="System Status" subtitle="LIVE PROBE vs STORED LABEL" testid="twin-status">
          <div className="space-y-2">
            {nodes.map((n) => (
              <div key={n.id} className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-2.5" data-testid={`twin-row-${n.id}`}>
                <Cpu className="h-4 w-4 text-white/40" />
                <div className="min-w-0">
                  <div className="text-sm text-white/80">{n.label}</div>
                  <div className="font-mono text-[8.5px] uppercase tracking-wider text-white/30">{n.sub}</div>
                </div>
                <span className="ml-auto flex items-center gap-1.5 text-right text-[10.5px]" style={{ color: n.color }} data-testid={`twin-badge-${n.id}`}>
                  <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${n.live ? "nx-pulse" : ""}`}
                    style={n.hollow ? { border: `1px dashed ${n.color}` } : { background: n.color }} />
                  {n.badge}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-3 font-mono text-[9px] leading-relaxed text-white/35">
            Queue labels are set in the Integration Provisioning Queue; they are not connection tests. A node turns LIVE only when a live connector or probe backs it.
          </p>
        </Panel>
        <Panel title="Hosting Environment" subtitle={hostErr ? "SELF-PROBE UNAVAILABLE" : "LIVE SELF-PROBE · DATA CENTER NODE"} testid="twin-hosting"
          right={<Pill tone={host?.status === "live" && !hostErr ? "emerald" : "amber"}>{hostErr ? "UNAVAILABLE" : host ? (host.status === "live" ? "LIVE" : "DEGRADED") : "PROBING"}</Pill>}>
          <div className="grid grid-cols-2 gap-3">
            <Mini label="DB Ping" value={dbi.reachable ? `${dbi.ping_ms} ms` : host ? "DOWN" : "—"} />
            <Mini label="Orchestrator" value={rt.orchestrator ? (rt.orchestrator.startsWith("Kubernetes") ? "K8s" : rt.orchestrator) : "—"} />
            <Mini label="Compute Limit" value={rt.cpu_limit_vcpu ? `${rt.cpu_limit_vcpu} vCPU · ${rt.memory_limit_gib ?? "?"} GiB` : "—"} />
            <Mini label="Uptime" value={fmtUptime(rt.process_uptime_s)} />
          </div>
          <div className="mt-3 space-y-1 font-mono text-[9.5px] leading-relaxed text-white/45" data-testid="twin-hosting-detail">
            <div>PROVIDER · {host?.provider ? `${host.provider.name}${host.provider.region ? ` · ${host.provider.region}` : ""}` : "—"}{" "}
              {host?.provider && <span className={host.provider.basis === "detected" ? "text-emerald-300/70" : "text-amber-300/70"}>({host.provider.basis.toUpperCase()})</span>}</div>
            {host?.provider?.platform && host.provider.platform !== host.provider.name && <div>PLATFORM · {host.provider.platform}</div>}
            <div>DATABASE · {dbi.engine || "—"}</div>
            <div>RUNTIME · {[rt.architecture, rt.os].filter(Boolean).join(" · ") || "—"}</div>
            <div>CHECKED · {host?.checked_at ? new Date(host.checked_at).toLocaleTimeString() : "—"}</div>
            {host?.provider?.note && <div className="text-white/30">{host.provider.note}.</div>}
          </div>
        </Panel>
        <Panel title="Data Flow" subtitle="COUNTS · NO SYNTHETIC THROUGHPUT" testid="twin-flow">
          <div className="grid grid-cols-2 gap-3">
            <Mini label="Live-Probed Nodes" value={`${liveCount}`} />
            <Mini label="Queue-Labelled Nodes" value={`${queueLabelled}`} />
          </div>
        </Panel>
      </div>
    </div>
  );
}

/* ==================== PREDICTIVE ==================== */
export function PredictiveView({ runCommand }) {
  const [pred, setPred] = useState(null);
  useEffect(() => {
    nexsysApi.predictive()
      .then((d) => { if (d?.source && d.source !== "synthetic" && d.tiles) setPred(d); })
      .catch(() => {});
  }, []);
  if (!pred) {
    return (
      <Panel title="Predictive Analytics" subtitle="NO LIVE DATA SOURCE" testid="pred-empty">
        <p className="text-sm text-white/60">No live data source is connected, so no projections are shown. Projections appear once a live source provides a baseline.</p>
      </Panel>
    );
  }
  const tiles = pred.tiles;
  const scenarios = pred.scenarios.map((s) => ({ name: s.name, done: s.on_time, color: s.color, basis: s.basis }));
  const forecast = pred.forecast;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-white/35">Scenario modeling</span>
        <Pill tone={pred ? "emerald" : "amber"} data-testid="pred-source-pill">
          {pred
            ? `MODELED FROM LIVE ${(pred.sourceName || pred.source).toUpperCase()} BASELINE · PROJECTIONS, NOT MEASUREMENTS`
            : "MODELED / PROJECTED · SAMPLE DATA (NO LIVE CONNECTOR)"}
        </Pill>
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {tiles.map((t) => (
          <Tile key={t.testid} label={t.label} value={t.value} suffix={t.suffix} tone={t.tone} testid={t.testid}
            sub={t.basis} decimals={pred ? 1 : 0} />
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        <Panel title="Forecast Confidence Band" subtitle={pred ? "14-WEEK PROJECTION · MODELED FROM LIVE BASELINE" : "14-WEEK PROJECTION · MODELED (SAMPLE)"} testid="pred-band"
          right={<button onClick={() => runCommand("Run a scenario simulation.")} className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-[11px] text-cyan-300">Run Simulation</button>}>
          <ForecastBand data={forecast} />
          <div className="mt-2 flex justify-center gap-5 font-mono text-[10px] text-white/40">
            <span className="text-cyan-300">{pred ? "— ACTUAL (CURRENT LIVE COMPLETION)" : "— ACTUAL"}</span><span className="text-indigo-300">-- FORECAST</span><span>▨ CONFIDENCE INTERVAL</span>
          </div>
        </Panel>
        <Panel title="Scenario Comparison" subtitle={pred ? "ON-TIME DELIVERY · MODELED ON LIVE BASELINE" : "ON-TIME DELIVERY · MODELED (SAMPLE)"} testid="pred-scenarios">
          <div className="space-y-4 py-2">
            {scenarios.map((s, i) => (
              <div key={s.name} title={s.basis || ""}>
                <div className="mb-1.5 flex justify-between text-xs"><span className="text-white/70">{s.name}</span><span className="font-mono" style={{ color: s.color }}>{s.done}%</span></div>
                <div className="h-2.5 overflow-hidden rounded-full bg-white/5">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${s.done}%` }} transition={{ delay: i * 0.12, duration: 0.9 }}
                    className="h-full rounded-full" style={{ background: s.color, boxShadow: `0 0 10px ${s.color}` }} />
                </div>
                {s.basis && <div className="mt-1 font-mono text-[9px] uppercase tracking-wider text-white/30">{s.basis}</div>}
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

/* ==================== GOVERNANCE ==================== */
function useGovernanceStatus() {
  const [state, setState] = useState({ loading: true, data: null });
  useEffect(() => {
    let alive = true;
    nexsysApi.governanceStatus()
      .then((d) => { if (alive) setState({ loading: false, data: d }); })
      .catch(() => { if (alive) setState({ loading: false, data: null }); });
    return () => { alive = false; };
  }, []);
  return state;
}

export function GovernanceView() {
  const { loading, data } = useGovernanceStatus();
  if (loading) {
    return <div className="py-10 text-center text-sm text-white/50" data-testid="gov-loading">Reading governed state…</div>;
  }
  if (!data) {
    return (
      <Panel title="Governance" subtitle="UNVERIFIED" testid="gov-unavailable">
        <p className="text-sm text-white/70">Governed status could not be retrieved. Nothing is shown as verified.</p>
      </Panel>
    );
  }
  const reg = data.model_registry || { configured: false, models: [] };
  const mon = data.monitoring || {};
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Panel title="AI Governance Coverage" subtitle="FROM GOVERNED STATE" testid="gov-coverage">
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <Pill tone="amber">Unverified</Pill>
          <p className="max-w-xs text-[13px] text-white/60" data-testid="gov-coverage-note">{data.coverage_note}</p>
        </div>
      </Panel>
      <Panel title="Model Registry" subtitle="GOVERNED AI MODELS" className="lg:col-span-2" testid="gov-registry">
        {reg.configured ? (
          <div className="space-y-2">
            {reg.models.map((m, i) => (
              <div key={m.id || m.name || i} className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                <Cpu className="h-4 w-4 text-cyan-400/70" />
                <span className="text-sm text-white/85">{m.name || "Unnamed model"}</span>
                <span className="ml-auto font-mono text-[11px] text-white/40">{m.env || m.environment || ""}</span>
                <Pill tone="cyan">{m.status || "Status not recorded"}</Pill>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid="gov-registry-empty">
            <Cpu className="h-4 w-4 text-white/40" />
            <span className="text-sm text-white/70">No governed model registry configured.</span>
          </div>
        )}
      </Panel>
      <Panel title="Governance Controls" subtitle="CONFIGURATION STATE · NOT A COMPLIANCE ATTESTATION" className="lg:col-span-3" testid="gov-policies">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {(data.controls || []).map((c) => {
            const ok = c.state === "configured";
            return (
              <div key={c.key} className="flex items-start gap-2.5 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5" data-testid={`gov-control-${c.key}`}>
                {ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-400" /> : <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-400" />}
                <div>
                  <div className="text-[13px] text-white/80">{c.name}</div>
                  <div className="text-[11px] text-white/45">{ok ? "Configured" : "Not configured"} · {c.detail}</div>
                </div>
              </div>
            );
          })}
        </div>
      </Panel>
      <Panel title="Governance Monitoring" subtitle="AUTOMATED CHECKS AND ALERTS" className="lg:col-span-3" testid="gov-monitoring">
        <div className="flex flex-wrap items-start gap-3">
          <Pill tone={mon.implemented ? "emerald" : "amber"}>{mon.implemented ? "Checks recorded" : "Not implemented"}</Pill>
          <div className="min-w-[240px] flex-1 space-y-1 text-[13px] text-white/65">
            <p data-testid="gov-monitoring-note">{mon.implemented ? `Last recorded check: ${mon.last_check_at}` : mon.note}</p>
            <p className="text-white/45">Only alert in place: {mon.alert_scope}. Alerts are not persisted to governed state.</p>
          </div>
        </div>
      </Panel>
    </div>
  );
}

/* ==================== AI SIGNALS ==================== */
export function SignalsView({ insights }) {
  const ins = insights || { risks: [], recommendations: [], opportunities: [] };
  if (!ins.risks_source) {
    return (
      <Panel title="AI Signal Stream" subtitle="NO LIVE DATA SOURCE" testid="signals-stream">
        <p className="text-sm text-white/60">No live data source is connected, so no signals are shown.</p>
      </Panel>
    );
  }
  const stream = ins.risks.slice(0, 5);
  const fmtConf = (r) => {
    const v = r.confidence ?? r.level;
    return typeof v === "number" ? `${v}%` : (v || "");
  };
  return (
    <Panel title="AI Signal Stream" subtitle={`LIVE · ${ins.risks_source.toUpperCase()}`} testid="signals-stream" right={<Pill tone="cyan">LIVE</Pill>}>
      <div className="space-y-2">
        {stream.map((r, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
            className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
            <span className="h-2 w-2 rounded-full nx-pulse" style={{ background: i % 2 ? "#ff9f43" : "#22d3ee" }} />
            <span className="text-[13px] text-white/80">{r.title}</span>
            <span className="ml-auto font-mono text-[11px] text-cyan-300">{fmtConf(r)}</span>
          </motion.div>
        ))}
      </div>
    </Panel>
  );
}
